// Cart drawer behaviour (global mini-cart):
//   - Opens on add-to-cart (the `cart:added` event from cart-actions.js) as a
//     non-redirecting confirmation, and on tapping the header cart bag.
//   - Quantity − / + and remove per line, re-rendered via the Section Rendering
//     API (window.toyCart.changeCartLine) — Liquid stays the source of truth.
//   - Stays in sync with cart changes elsewhere via the global `cart:updated`
//     event (only re-renders while open, to avoid needless fetches).
//
// Uses <dialog> for the top layer + focus trap + Esc, matching the mobile-nav
// and search overlays. Falls back gracefully if showModal is unavailable.

(function () {
  function getDrawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function getContents(drawer) {
    return drawer.querySelector('[data-cart-contents]');
  }

  function getSectionId(drawer) {
    const c = getContents(drawer);
    return c ? c.dataset.sectionId : null;
  }

  // Swap the drawer's contents via the shared Section Rendering helper, scoped
  // to the drawer so it never grabs the cart page's [data-cart-contents] region.
  function renderContents(drawer, html) {
    return window.toyCart.renderSection('[data-cart-contents]', html, drawer);
  }

  // Re-fetch + swap the drawer contents via Section Rendering GET (?sections=).
  // `cache: 'no-store'` is essential: the browser/CDN can serve a cached GET that
  // predates the just-added item, leaving the drawer showing an empty cart. The
  // add flow itself uses uncached POST (bundled) section rendering; this GET is
  // only the fallback / open-from-header path.
  async function refresh(drawer) {
    const sectionId = getSectionId(drawer);
    if (!sectionId) return;
    try {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      renderContents(drawer, data[sectionId]);
    } catch (error) {
      console.warn('[cart-drawer] refresh failed:', error);
    }
  }

  function openDrawer(drawer) {
    if (drawer.open) return;
    if (typeof drawer.showModal === 'function') {
      try {
        drawer.showModal();
      } catch (error) {
        drawer.setAttribute('open', '');
      }
    } else {
      drawer.setAttribute('open', '');
    }
  }

  // CSS (transition + allow-discrete) animates the close, so just close it.
  function closeDrawer(drawer) {
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
  }

  async function handleAction(drawer, btn) {
    if (!window.toyCart) return;
    const lineEl = btn.closest('[data-line-key]');
    if (!lineEl) return;
    const key = lineEl.dataset.lineKey;
    if (!key) return;

    const action = btn.dataset.cartAction;
    const qtyEl = lineEl.querySelector('[data-line-quantity]');
    const current = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;

    let nextQty;
    if (action === 'increment') nextQty = current + 1;
    else if (action === 'decrement') nextQty = current - 1;
    else if (action === 'remove') nextQty = 0;
    else return;
    if (nextQty < 0) return;

    window.toyCart.setBusy(btn, '');
    window.toyCart.setLineBusy(lineEl, true);

    try {
      const sectionId = getSectionId(drawer);
      const cart = await window.toyCart.changeCartLine(key, nextQty, sectionId);
      const html = sectionId && cart.sections ? cart.sections[sectionId] : null;
      if (!renderContents(drawer, html)) {
        await refresh(drawer);
      }
    } catch (error) {
      console.error('[cart-drawer] line change failed:', error);
      window.toyCart.setError(btn, error.message);
      window.toyCart.setLineBusy(lineEl, false);
    }
  }

  function init() {
    const drawer = getDrawer();
    if (!drawer) return;

    // Open from the header cart bag — open instantly, refresh in the background
    // (its server-rendered contents are already current on page load).
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-drawer-trigger]');
      if (!trigger) return;
      event.preventDefault();
      openDrawer(drawer);
      refresh(drawer);
    });

    // Close: backdrop click + any close control.
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) {
        closeDrawer(drawer);
        return;
      }
      const closeBtn = event.target.closest('[data-cart-drawer-close]');
      if (closeBtn) {
        event.preventDefault();
        closeDrawer(drawer);
      }
    });

    // Esc uses the dialog's native close, which now slides out via CSS.

    // Line actions — delegated so they survive every contents swap.
    drawer.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cart-action]');
      if (btn && !btn.disabled) {
        event.preventDefault();
        handleAction(drawer, btn);
      }
    });

    // Add flow: fill the drawer and slide it out — so the item is already there
    // when it appears. Prefer the freshly rendered section HTML the add request
    // returned (bundled section rendering, uncached POST), which fixes the drawer
    // showing empty until reload. Fall back to a no-store GET refresh if absent.
    document.addEventListener('cart:added', (event) => {
      const sectionId = getSectionId(drawer);
      const sections = event.detail && event.detail.sections;
      const html = sections && sectionId ? sections[sectionId] : null;
      if (html && renderContents(drawer, html)) {
        openDrawer(drawer);
      } else {
        refresh(drawer).then(() => openDrawer(drawer));
      }
    });
    // Passively keep in sync on any other cart change while open.
    document.addEventListener('cart:updated', () => {
      if (drawer.open) refresh(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
